/**
 * 前台用户状态管理
 * 使用 Zustand 管理前台应用的全局状态
 *
 * 命名规范：
 * - 前台使用 userStore
 * - 后台使用 adminStore（位于 stores/adminStore.ts）
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  userService,
  type User,
  type LoginCredentials,
  type RegisterData,
  type UserUpdateData,
} from "@/services/userService";
import { ApiException } from "@/lib/userApiClient";

// 用户状态类型定义
interface UserState {
  // 状态
  isAuthenticated: boolean;
  currentUser: User | null;
  isLoading: boolean;
  error: string | null;

  // 操作方法
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  fetchUser: () => Promise<void>;
  updateUser: (data: UserUpdateData) => Promise<void>;
  updateCoverImage: (file: File) => Promise<void>;
  clearError: () => void;
  setUser: (user: User) => void;
}

/**
 * 前台用户状态 Store
 *
 * 功能：
 * - 用户认证状态管理
 * - 当前用户信息管理
 * - 登录/登出/注册
 * - 用户信息更新
 * - 错误处理
 *
 * 注意：
 * - 使用 session 认证（cookie-based）
 * - 不需要管理 token（与后台 adminStore 的 JWT 认证不同）
 * - 持久化部分状态到 localStorage
 */
export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      // 初始状态
      isAuthenticated: false,
      currentUser: null,
      isLoading: false,
      error: null,

      /**
       * 登录
       */
      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null });
        try {
          const user = await userService.login(credentials);
          set({
            isAuthenticated: true,
            currentUser: user,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const message =
            error instanceof ApiException
              ? error.message
              : "登录失败，请稍后再试";
          set({
            isAuthenticated: false,
            currentUser: null,
            isLoading: false,
            error: message,
          });
          throw error;
        }
      },

      /**
       * 注册
       */
      register: async (data: RegisterData) => {
        set({ isLoading: true, error: null });
        try {
          const user = await userService.register(data);
          set({
            isAuthenticated: true,
            currentUser: user,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const message =
            error instanceof ApiException
              ? error.message
              : "注册失败，请稍后再试";
          set({
            isAuthenticated: false,
            currentUser: null,
            isLoading: false,
            error: message,
          });
          throw error;
        }
      },

      /**
       * 登出
       */
      logout: async () => {
        set({ isLoading: true, error: null });
        try {
          await userService.logout();
          set({
            isAuthenticated: false,
            currentUser: null,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          // 即使登出失败，也清除本地状态
          set({
            isAuthenticated: false,
            currentUser: null,
            isLoading: false,
            error: "登出失败",
          });
        }
      },

      /**
       * 检查认证状态
       * 用于页面加载时验证 session 是否有效
       */
      checkAuth: async () => {
        set({ isLoading: true, error: null });
        try {
          const user = await userService.checkAuth();
          if (user) {
            set({
              isAuthenticated: true,
              currentUser: user,
              isLoading: false,
              error: null,
            });
          } else {
            set({
              isAuthenticated: false,
              currentUser: null,
              isLoading: false,
              error: null,
            });
          }
        } catch (error) {
          set({
            isAuthenticated: false,
            currentUser: null,
            isLoading: false,
            error: null,
          });
        }
      },

      /**
       * 刷新用户信息
       * 用于更新用户数据（如签到后更新积分）
       */
      fetchUser: async () => {
        try {
          const user = await userService.checkAuth();
          if (user) {
            set({
              currentUser: user,
            });
          }
        } catch (error) {
          console.error("Failed to fetch user:", error);
        }
      },

      /**
       * 更新用户信息
       */
      updateUser: async (data: UserUpdateData) => {
        set({ isLoading: true, error: null });
        try {
          const user = await userService.updateCurrentUser(data);
          set({
            currentUser: user,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const message =
            error instanceof ApiException
              ? error.message
              : "更新失败，请稍后再试";
          set({
            isLoading: false,
            error: message,
          });
          throw error;
        }
      },

      /**
       * 更新封面图
       */
      updateCoverImage: async (file: File) => {
        set({ isLoading: true, error: null });
        try {
          const coverImageUrl = await userService.updateCoverImage(file);
          if (get().currentUser) {
            set((state) => ({
              currentUser: {
                ...state.currentUser!,
                cover_image_url: coverImageUrl,
              },
              isLoading: false,
            }));
          }
        } catch (error) {
          const message =
            error instanceof ApiException ? error.message : "更新封面失败";
          set({
            isLoading: false,
            error: message,
          });
          throw error;
        }
      },

      /**
       * 设置用户信息（用于外部更新）
       */
      setUser: (user: User) => {
        set({
          isAuthenticated: true,
          currentUser: user,
        });
      },

      /**
       * 清除错误信息
       */
      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: "user-storage", // localStorage key（与 admin-storage 区分）
      storage: createJSONStorage(() => localStorage),
      // 只持久化部分状态
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        currentUser: state.currentUser,
      }),
    },
  ),
);

// 导出便捷的选择器 hooks
export const useCurrentUser = () => useUserStore((state) => state.currentUser);
export const useIsAuthenticated = () =>
  useUserStore((state) => state.isAuthenticated);
export const useUserLoading = () => useUserStore((state) => state.isLoading);
export const useUserError = () => useUserStore((state) => state.error);

export default useUserStore;
